import { useCallback, useEffect, useState } from "react";
import { Alert, Button, Spinner } from "react-bootstrap";
import * as api from "../api/client";
import DataTable from "../components/DataTable";
import UserFormModal from "../components/UserFormModal";

function fmtDate(epochSeconds) {
  return epochSeconds ? new Date(epochSeconds * 1000).toLocaleString() : "—";
}

// Admin-only. Lists users in a searchable/paginated DataTable and creates/edits them through a modal
// (see UserFormModal) rather than an inline form — the inline form couldn't hold a password-reset field
// without it being confusingly always-visible on every row.
export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [modal, setModal] = useState(null); // { mode: "create" | "edit", user? }
  const [saving, setSaving] = useState(false);
  const [modalError, setModalError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [u, r] = await Promise.all([api.listUsers(), api.listRoles()]);
      setUsers(u.users || []);
      setRoles(r.roles || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  function closeModal() {
    setModal(null);
    setModalError("");
  }

  async function handleSubmit(payload) {
    setSaving(true);
    setModalError("");
    try {
      if (modal.mode === "edit") {
        await api.updateUser(modal.user.id, payload);
      } else {
        await api.createUser(payload);
      }
      closeModal();
      await load();
    } catch (e) {
      setModalError(e.message);
    } finally {
      setSaving(false);
    }
  }

  async function removeUser(id, uname) {
    if (!window.confirm(`Delete user "${uname}"? This cannot be undone.`)) return;
    setError("");
    try {
      await api.deleteUser(id);
      await load();
    } catch (e) {
      setError(e.message);
    }
  }

  const columns = [
    { key: "username", label: "Username", sortable: true },
    {
      key: "roles",
      label: "Roles",
      sortable: false,
      render: (u) =>
        (u.roles || []).length ? (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {u.roles.map((r) => (
              <span key={r} className="pill neutral">
                {r}
              </span>
            ))}
          </div>
        ) : (
          <span style={{ color: "var(--muted)" }}>— none —</span>
        ),
    },
    {
      key: "isAdmin",
      label: "Admin",
      sortable: true,
      render: (u) => (u.isAdmin ? <span className="pill success">admin</span> : ""),
    },
    { key: "createdAt", label: "Created", sortable: true, render: (u) => fmtDate(u.createdAt) },
    {
      key: "actions",
      label: "Actions",
      align: "right",
      render: (u) => (
        <div className="dt-actions">
          <button className="dt-icon-btn" title="Edit" onClick={() => setModal({ mode: "edit", user: u })}>
            ✎
          </button>
          <button className="dt-icon-btn danger" title="Delete" onClick={() => removeUser(u.id, u.username)}>
            🗑
          </button>
        </div>
      ),
    },
  ];

  return (
    <main className="main">
      <div className="topbar">
        <h1>Users</h1>
        <p>
          Create users and assign them one or more roles. A user can query every table and read every document
          that any of their roles grants.
        </p>
      </div>
      <div className="page-body">
        {error && (
          <Alert variant="danger" onClose={() => setError("")} dismissible>
            {error}
          </Alert>
        )}

        <div className="page-head-row">
          <Button variant="success" onClick={() => setModal({ mode: "create" })}>
            + Create user
          </Button>
        </div>

        {loading ? (
          <Spinner animation="border" />
        ) : (
          <DataTable
            columns={columns}
            rows={users.map((u) => ({ ...u, rolesText: (u.roles || []).join(" ") }))}
            searchKeys={["username", "rolesText"]}
            searchPlaceholder="Search users..."
            emptyMessage="No users yet — create one above."
          />
        )}

        {modal && (
          <UserFormModal
            mode={modal.mode}
            initial={modal.user}
            roles={roles}
            saving={saving}
            error={modalError}
            onClose={closeModal}
            onSubmit={handleSubmit}
          />
        )}
      </div>
    </main>
  );
}
